/**
 * The individual sections of the pickup column, in the order the column stacks
 * them. Split out of QuotesPickupColumn: every one takes the same props and
 * renders one labelled block, so the parent is left owning the order and the
 * two conditions that hide a section (no contact needed at some distribution
 * warehouses, no loading capabilities when a liftgate is already on the quote).
 */

import { nameAccessorials } from "@/Data/nameAccessorials";
import { formatHandlingUnitTypeWithQuantity } from "@/Data/formatHandlingUnitTypeWithQuantity";
import { describeLocationType } from "@/Data/describeLocationType";
import { dangerousGoodsDescribeHazardClass } from "@/Shared/DangerousGoods/describeHazardClass";
import { quotesFormatLocation } from "../../Helpers/quotesFormatLocation";
import { describeHours } from "@/Helpers/describeHours";
import { formatPhone } from "@/Helpers/formatPhone";
import { isDomesticCanada } from "@/Helpers/isDomesticCanada";
import {
  totalWeightForLineItem,
  totalWeightForLineItems,
} from "@/Helpers/lineItemCalculations";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { TemperatureHandling } from "@freightsimple/generated-dashboard-openapi-client/models/TemperatureHandling";
import { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import { QuotesColumnSectionHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnSectionHeader";
import { QuotesLineItemText1 } from "@/Shared/Quotes/CustomComponents/QuotesLineItemText1";
import { QuotesLineItemText2 } from "@/Shared/Quotes/CustomComponents/QuotesLineItemText2";
import { QuotesTextLink } from "@/Shared/Quotes/CustomComponents/QuotesTextLink";
import { QuotesLocationTypeDescription } from "@/Shared/Quotes/CustomComponents/QuotesLocationTypeDescription";
import { QuotesModifyLink } from "@/Shared/Quotes/CustomComponents/QuotesModifyLink";
import { QuotesPrimaryText } from "@/Shared/Quotes/CustomComponents/QuotesPrimaryText";
import { QuotesSecondaryText } from "@/Shared/Quotes/CustomComponents/QuotesSecondaryText";
import moment from "moment";
import { describeLoadingCapabilities } from "@/Shared/Locations/Helpers/loadingCapabilities";
import type { PickupColumnProps } from "../QuotesPickupColumn";
export function WhenSection_(props: PickupColumnProps) {
  const { pickupLocation, pickupDate, pickupDeadline } = props;

  const formattedDate = pickupDate.format("dddd Do MMMM YYYY");
  const windowDescription = pickupLocation.hours
    ? `Pickup window: ${describeHours(pickupLocation.hours)}`
    : undefined;

  const onHold = props.shipmentState === ShipmentState.OnHold;

  const formattedPickupDeadlineDate = props.pickupDeadline
    ? moment(pickupDeadline).format("dddd Do MMMM YYYY")
    : "";

  return (
    <>
      <QuotesColumnSectionHeader>When</QuotesColumnSectionHeader>
      {!onHold && <QuotesPrimaryText>{formattedDate}</QuotesPrimaryText>}
      {onHold && <QuotesPrimaryText>On Hold</QuotesPrimaryText>}
      {!onHold && !props.hidePickupHours && windowDescription && (
        <QuotesSecondaryText>{windowDescription}</QuotesSecondaryText>
      )}
      {(props.shipmentState === ShipmentState.Quoted ||
        props.shipmentState === ShipmentState.BookingConfirmed) &&
        props.onChangePickupDate && (
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onChangePickupDate}>
              Change pickup date
            </QuotesTextLink>
          </QuotesModifyLink>
        )}

      {pickupDeadline && !onHold && (
        <>
          <QuotesColumnSectionHeader>Pickup Deadline</QuotesColumnSectionHeader>
          <QuotesPrimaryText>{formattedPickupDeadlineDate}</QuotesPrimaryText>
        </>
      )}
    </>
  );
}

export function WhereSection_(props: PickupColumnProps) {
  const { pickupLocation } = props;

  const locationTypeDescription = describeLocationType(
    pickupLocation.locationType,
    pickupLocation.distributionWarehouseBrand,
  );
  const formattedLocation = quotesFormatLocation(pickupLocation);

  if (pickupLocation.businessName === undefined) {
    return (
      <>
        <QuotesColumnSectionHeader>Where</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{formattedLocation}</QuotesPrimaryText>
        <QuotesSecondaryText>{locationTypeDescription}</QuotesSecondaryText>
      </>
    );
  } else {
    return (
      <>
        <QuotesColumnSectionHeader>Where</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{pickupLocation.businessName}</QuotesPrimaryText>
        <QuotesSecondaryText>
          Location Type:{" "}
          <QuotesLocationTypeDescription>
            {locationTypeDescription}
          </QuotesLocationTypeDescription>
        </QuotesSecondaryText>
        <QuotesSecondaryText>
          {pickupLocation.address.addressLine}
        </QuotesSecondaryText>
        <QuotesSecondaryText>
          {pickupLocation.address.addressLine2}
        </QuotesSecondaryText>
        <QuotesSecondaryText>{formattedLocation}</QuotesSecondaryText>
        {props.onModifyPickupAddress && (
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onModifyPickupAddress}>
              Change address
            </QuotesTextLink>
          </QuotesModifyLink>
        )}
      </>
    );
  }
}

export function ContactSection_(props: PickupColumnProps) {
  const { pickupContact } = props;

  if (pickupContact?.contactName === undefined) {
    return <></>;
  } else {
    return (
      <>
        <QuotesColumnSectionHeader>Contact</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{pickupContact?.contactName}</QuotesPrimaryText>
        <QuotesSecondaryText>{pickupContact?.emailAddress}</QuotesSecondaryText>
        <QuotesSecondaryText>
          {formatPhone(
            pickupContact.phoneNumber,
            pickupContact.phoneNumberExtension,
          )}
        </QuotesSecondaryText>
        {props.onModifyPickupContact && (
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onModifyPickupContact}>
              Change contact
            </QuotesTextLink>
          </QuotesModifyLink>
        )}
      </>
    );
  }
}

export function SpecialServicesSection_(props: PickupColumnProps) {
  const { pickupLocation } = props;

  const lines = nameAccessorials(
    pickupLocation.accessorials,
    LocationContext.Pickup,
    pickupLocation.locationType,
  );

  return (
    <>
      <QuotesColumnSectionHeader>Special Services</QuotesColumnSectionHeader>
      {/* Text styles live on the li (not a child div) so the bullet marker
       * matches the text's size and colour instead of floating at the
       * inherited 16px black. */}
      <ul className="mb-0 ps-5">
        {lines.map((line) => {
          return (
            <li
              key={line}
              className="text-[14px] font-light text-content-muted"
            >
              {line}
            </li>
          );
        })}
      </ul>
      {props.onModifyDetails && (
        <>
          <div className="h-2" />
          <QuotesModifyLink>
            <QuotesTextLink onClick={props.onModifyDetails}>
              Modify special services
            </QuotesTextLink>
          </QuotesModifyLink>
        </>
      )}
    </>
  );
}

export function LoadingCapabilitiesSection_(props: PickupColumnProps) {
  if (
    props.pickupLocationHasDock === undefined &&
    props.pickupLocationHasForklift === undefined
  ) {
    return <></>;
  }

  const locationHasDock =
    props.pickupLocationHasDock ?? LoadingCapabilitiesAvailability.Unknown;
  const locationHasForklift =
    props.pickupLocationHasForklift ?? LoadingCapabilitiesAvailability.Unknown;

  const description = describeLoadingCapabilities(
    locationHasDock,
    locationHasForklift,
  );

  return (
    <>
      <QuotesColumnSectionHeader>Loading</QuotesColumnSectionHeader>
      <QuotesSecondaryText>{description}</QuotesSecondaryText>
    </>
  );
}

export function EquipmentSection_(props: PickupColumnProps) {
  const { equipmentType, linearFeet, exclusiveUseNeeded, tarpRequired } = props;

  function describeEquipmentType() {
    switch (equipmentType) {
      case EquipmentType.DryVan:
        return "Dry Van";
      case EquipmentType.FlatBed:
        return "Flatbed";
      case EquipmentType.Reefer:
        return "Temperature Controlled";
      case undefined:
        return "";
    }
  }

  function describeEquipment() {
    const etDescription = describeEquipmentType();
    const sharedDescription = exclusiveUseNeeded ? "Dedicated" : "Shared";
    const linearFeetDescription = exclusiveUseNeeded
      ? ""
      : `, ${linearFeet}ft required`;
    const tarpDescription =
      equipmentType === EquipmentType.FlatBed
        ? tarpRequired
          ? " (Tarp Required)"
          : " (Tarp Not Required)"
        : "";

    return `${sharedDescription} ${etDescription}${tarpDescription}${linearFeetDescription}`;
  }

  if (props.equipmentType === undefined) {
    return <></>;
  }

  return (
    <>
      <QuotesColumnSectionHeader>Equipment</QuotesColumnSectionHeader>
      <QuotesSecondaryText>{describeEquipment()}</QuotesSecondaryText>
    </>
  );
}

export function PayloadSection_(props: PickupColumnProps) {
  const { lineItems } = props;

  const formattedLineItems = lineItems.map((li) => {
    const secondary = `${formatHandlingUnitTypeWithQuantity(
      li.numberHandlingUnits,
      li.handlingUnitType,
    )} ${li.description}`;

    let tertiary = `${li.length}" x ${li.width}" x ${li.height}"`;

    if (lineItems.length > 1) {
      tertiary += ` - ${totalWeightForLineItem(li)}lb`;
    }

    if (li.isStackable) {
      tertiary += ` - Stackable`;
    } else {
      tertiary += ` - Not Stackable`;
    }

    let freightClass = undefined;

    if (li.temperatureHandling === TemperatureHandling.ProtectFromFreezing) {
      tertiary += ` - Protect From Freezing`;
    }

    if (li.temperatureHandling === TemperatureHandling.KeepRefrigerated) {
      tertiary += ` - Keep Refrigerated`;
    }

    if (li.temperatureHandling === TemperatureHandling.KeepFrozen) {
      tertiary += ` - Keep Frozen`;
    }

    if (li.freightClass && !isDomesticCanada) {
      freightClass = `Freight class ${li.freightClass}`;
    }

    let dangerous = undefined;

    if (li.isDangerous && li.dangerousClassification) {
      dangerous = `Dangerous: ${
        li.dangerousUnNumber
      }, ${dangerousGoodsDescribeHazardClass(li.dangerousClassification)}`;
    }

    let nmfcItemNumber = undefined;

    if (li.nmfcItemNumber) {
      nmfcItemNumber = `NMFC: ${li.nmfcItemNumber}`;
    }

    return {
      secondary,
      tertiary,
      freightClass,
      nmfcItemNumber,
      dangerous,
    };
  });

  return (
    <>
      <QuotesColumnSectionHeader>Payload</QuotesColumnSectionHeader>
      <QuotesPrimaryText>
        Total Weight: {totalWeightForLineItems(props.lineItems)}lb
      </QuotesPrimaryText>
      {formattedLineItems.map((li) => {
        return (
          <div
            key={`${li.secondary}-${li.tertiary}`}
            className="mb-1 flex flex-col flex-nowrap items-start"
          >
            <QuotesLineItemText1>{li.secondary}</QuotesLineItemText1>
            <QuotesLineItemText2>{li.tertiary}</QuotesLineItemText2>
            {li.freightClass && (
              <QuotesLineItemText2>{li.freightClass}</QuotesLineItemText2>
            )}
            {li.nmfcItemNumber && (
              <QuotesLineItemText2>{li.nmfcItemNumber}</QuotesLineItemText2>
            )}
            {li.dangerous && (
              <QuotesLineItemText2>{li.dangerous}</QuotesLineItemText2>
            )}
          </div>
        );
      })}
      <div className="h-1" />
      {props.onModifyDetails && (
        <QuotesModifyLink>
          <QuotesTextLink onClick={props.onModifyDetails}>
            Modify contents
          </QuotesTextLink>
        </QuotesModifyLink>
      )}
    </>
  );
}
